import React, { useEffect, useRef } from 'react';
import { useThree } from '@react-three/fiber';
import { useGLTF } from '@react-three/drei';
import * as THREE from 'three';
import {
  type CADPosition,
  type CADCenterOffset,
  type DisplayedCADPosition,
  type PointerCoordinates,
  createDisplayedCADPosition,
  isPointerSelectionClick,
  restoreOriginalCADPosition,
} from '../../../utils/cadCoordinates';
import { fitCamera } from './cameraFit';
import { createThicknessMaterial } from './thicknessPresentation';

type CADCanvasRect = Pick<DOMRect, 'height' | 'left' | 'top' | 'width'>;
type PointerSelectionStart = PointerCoordinates & { pointerId: number };

export const raycastCADModelPoint = (
  camera: THREE.Camera,
  model: THREE.Object3D,
  rect: CADCanvasRect,
  canvasPoint: PointerCoordinates,
  raycaster = new THREE.Raycaster(),
  pointer = new THREE.Vector2(),
): DisplayedCADPosition | null => {
  if (rect.width <= 0 || rect.height <= 0) return null;

  pointer.set(
    ((canvasPoint.x - rect.left) / rect.width) * 2 - 1,
    -((canvasPoint.y - rect.top) / rect.height) * 2 + 1,
  );
  // OrbitControls and Center can change parent/camera transforms between
  // frames, so update both matrix trees immediately before raycasting.
  camera.updateMatrixWorld();
  model.updateWorldMatrix(true, true);
  raycaster.setFromCamera(pointer, camera);
  const intersection = raycaster.intersectObject(model, true)[0];
  return intersection
    ? createDisplayedCADPosition(
        intersection.point.x,
        intersection.point.y,
        intersection.point.z,
      )
    : null;
};

/**
 * Inner component that loads and displays the GLB model, then auto-fits camera
 */
export function Model({
  url,
  wireframe,
  preserveMaterialColors,
  thicknessPresentation = false,
  onLoaded,
  onModelRadius,
  fitTrigger,
  centerOffset,
  selectionMode,
  onModelPointClick,
}: {
  url: string;
  wireframe: boolean;
  preserveMaterialColors: boolean;
  thicknessPresentation?: boolean;
  onLoaded: () => void;
  onModelRadius?: (r: number) => void;
  fitTrigger: number;
  centerOffset: CADCenterOffset;
  selectionMode: boolean;
  onModelPointClick?: (position: CADPosition) => void;
}) {
  const { scene: gltfScene } = useGLTF(url);
  const { camera, gl } = useThree();
  const fitted = useRef(false);
  const pointerDown = useRef<PointerSelectionStart | null>(null);

  const clonedScene = React.useMemo(() => {
    const cloned = gltfScene.clone(true);
    cloned.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        const cloneMaterial = (source: THREE.Material): THREE.Material => {
          if (thicknessPresentation && preserveMaterialColors
            && source instanceof THREE.MeshStandardMaterial) {
            return createThicknessMaterial(source, wireframe);
          }
          const material = source.clone();
          if ('wireframe' in material) {
            (material as THREE.MeshStandardMaterial).wireframe = wireframe;
          }
          if (material instanceof THREE.MeshStandardMaterial) {
            if (preserveMaterialColors) {
              material.flatShading = true;
            } else {
              // Blue material for visibility on dark background
              material.color = new THREE.Color(0x6aadff);
              material.metalness = 0.2;
              material.roughness = 0.5;
              material.emissive = new THREE.Color(0x111122);
              material.emissiveIntensity = 0.1;
            }
            material.needsUpdate = true;
          }
          return material;
        };
        child.material = Array.isArray(child.material)
          ? child.material.map(cloneMaterial)
          : cloneMaterial(child.material);
      }
    });
    return cloned;
  }, [gltfScene, preserveMaterialColors, thicknessPresentation, wireframe]);

  useEffect(() => () => {
    clonedScene.traverse((child) => {
      if (!(child instanceof THREE.Mesh)) return;
      const materials = Array.isArray(child.material)
        ? child.material
        : [child.material];
      materials.forEach((material) => material.dispose());
    });
  }, [clonedScene]);

  // Auto-fit camera after model is loaded and report model radius
  useEffect(() => {
    if (!fitted.current) {
      fitted.current = true;
      fitCamera(clonedScene, camera);
      onLoaded();
      // Compute bounding sphere radius for marker sizing
      const box = new THREE.Box3().setFromObject(clonedScene);
      const sphere = new THREE.Sphere();
      box.getBoundingSphere(sphere);
      if (sphere.radius > 0) onModelRadius?.(sphere.radius);
    }
  }, [clonedScene, camera, onLoaded, onModelRadius]);

  useEffect(() => {
    if (fitTrigger > 0) {
      fitCamera(clonedScene, camera);
    }
  }, [camera, clonedScene, fitTrigger]);

  const selectionActive = selectionMode && Boolean(onModelPointClick);

  useEffect(() => {
    if (!selectionActive) {
      pointerDown.current = null;
      return undefined;
    }

    const canvas = gl.domElement;
    const raycaster = new THREE.Raycaster();
    const pointer = new THREE.Vector2();

    const releasePointerCapture = (pointerId: number) => {
      try {
        if (canvas.hasPointerCapture(pointerId)) {
          canvas.releasePointerCapture(pointerId);
        }
      } catch (error) {
        console.warn('Failed to release CAD pointer capture:', error);
      }
    };

    const handlePointerDown = (event: PointerEvent) => {
      if (event.button !== 0 || pointerDown.current) return;

      pointerDown.current = {
        pointerId: event.pointerId,
        x: event.clientX,
        y: event.clientY,
      };
      try {
        canvas.setPointerCapture(event.pointerId);
      } catch (error) {
        console.warn('Failed to capture CAD pointer:', error);
      }
    };

    const handlePointerUp = (event: PointerEvent) => {
      const pointerDownPosition = pointerDown.current;
      if (
        !pointerDownPosition
        || pointerDownPosition.pointerId !== event.pointerId
      ) {
        return;
      }

      pointerDown.current = null;
      releasePointerCapture(event.pointerId);

      if (
        !onModelPointClick
        || !isPointerSelectionClick(
          pointerDownPosition,
          { x: event.clientX, y: event.clientY },
        )
      ) {
        return;
      }

      const rect = canvas.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) {
        console.warn(
          'CAD point selection skipped because the canvas has no layout size.',
        );
        return;
      }

      const displayedPosition = raycastCADModelPoint(
        camera,
        clonedScene,
        rect,
        { x: event.clientX, y: event.clientY },
        raycaster,
        pointer,
      );
      if (!displayedPosition) return;

      onModelPointClick(restoreOriginalCADPosition(
        displayedPosition,
        centerOffset,
      ));
    };

    const clearPointerSelection = (event: PointerEvent) => {
      if (pointerDown.current?.pointerId !== event.pointerId) return;
      releasePointerCapture(event.pointerId);
      pointerDown.current = null;
    };

    // Native canvas listeners are intentional: the loaded GLTF root is a
    // THREE.Scene, and R3F primitive handlers do not reach its nested meshes.
    canvas.style.cursor = 'crosshair';
    canvas.addEventListener('pointerdown', handlePointerDown);
    canvas.addEventListener('pointerup', handlePointerUp);
    canvas.addEventListener('pointercancel', clearPointerSelection);
    canvas.addEventListener('lostpointercapture', clearPointerSelection);
    return () => {
      if (pointerDown.current) {
        releasePointerCapture(pointerDown.current.pointerId);
        pointerDown.current = null;
      }
      canvas.style.cursor = '';
      canvas.removeEventListener('pointerdown', handlePointerDown);
      canvas.removeEventListener('pointerup', handlePointerUp);
      canvas.removeEventListener('pointercancel', clearPointerSelection);
      canvas.removeEventListener('lostpointercapture', clearPointerSelection);
    };
  }, [
    camera,
    centerOffset,
    clonedScene,
    gl,
    onModelPointClick,
    selectionActive,
  ]);

  return <primitive object={clonedScene} />;
}

/**
 * Loading fallback inside Canvas
 */
export function LoadingFallback() {
  return (
    <mesh rotation={[0, 0, 0]}>
      <boxGeometry args={[1, 1, 1]} />
      <meshStandardMaterial color="#4a90d9" wireframe transparent opacity={0.5} />
    </mesh>
  );
}
